import * as React from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import type { SortDirection } from '@/modules/drivers/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { ImiDeclarationItem } from '../../services/useImiDeclarationsService'
import {
  getImiDeclarationsColumns,
  type ImiDeclarationSortKey,
} from './columns'
import ImiDeclarationRow from './ImiDeclarationRow'
import styles from './ImiDeclarationsTable.module.css'

type DeclarationAction = 'print' | 'email' | 'withdraw' | 'submit'

// Punkt wyjscia dla wirtualizatora; realna wysokosc mierzy potem measureElement,
// bo dlugie nazwisko albo waska szerokosc potrafia zawinac wiersz do dwoch linii.
const ROW_HEIGHT_PX = 49

// table-fixed potrzebuje jawnych szerokosci. Procenty, a nie piksele: przy stalych
// px tabela konczyla sie na 1090 px i zostawiala pusty pas po prawej, zamiast
// wypelnic kontener. Kolumna zaznaczenia zostaje w px, reszta dzieli resztę
// w proporcjach zblizonych do tego, co table-auto dawalo wczesniej.
const COLUMN_WIDTHS = ['48px', '34%', '9%', '16%', '15%', '13%', '13%']

type ImiDeclarationsTableProps = {
  declarations: ImiDeclarationItem[]
  isLoading: boolean
  isFetching: boolean
  selectedDeclarationIds: string[]
  isSelectionLocked: boolean
  allVisibleSelected: boolean
  hasPartiallySelectedVisible: boolean
  isBusy: (declarationId: string) => boolean
  isWithdrawable: (item: ImiDeclarationItem) => boolean
  getStatusLabel: (item: ImiDeclarationItem) => string
  onToggleSelect: (declarationId: string) => void
  onToggleSelectAll: (checked: boolean) => void
  onAction: (action: DeclarationAction, item: ImiDeclarationItem) => void
}

const ImiDeclarationsTable: React.FC<ImiDeclarationsTableProps> = ({
  declarations,
  isLoading,
  isFetching,
  selectedDeclarationIds,
  isSelectionLocked,
  allVisibleSelected,
  hasPartiallySelectedVisible,
  isBusy,
  isWithdrawable,
  getStatusLabel,
  onToggleSelect,
  onToggleSelectAll,
  onAction,
}) => {
  const { t } = useLocalize()
  const selectAllRef = React.useRef<HTMLInputElement | null>(null)
  const [sortConfig, setSortConfig] = React.useState<{
    key: ImiDeclarationSortKey
    direction: SortDirection
  } | null>(null)

  const handleHeaderClick = React.useCallback((key: string) => {
    const isSortKey = (
      value: string,
    ): value is ImiDeclarationSortKey =>
      value === 'driver' ||
      value === 'country' ||
      value === 'start' ||
      value === 'end' ||
      value === 'status'
    if (!isSortKey(key)) return

    setSortConfig((current) => ({
      key,
      direction:
        current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  const sortedDeclarations = React.useMemo(() => {
    if (!sortConfig) return declarations

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    })
    const getValue = (item: ImiDeclarationItem) => {
      switch (sortConfig.key) {
        case 'driver':
          return item.driverLatinFullName || ''
        case 'country':
          return item.declarationPostingCountry || ''
        case 'start':
          return item.declarationStartDate || ''
        case 'end':
          return item.declarationEndDate || ''
        case 'status':
          return getStatusLabel(item)
      }
    }

    return [...declarations].sort((a, b) => {
      const aValue = getValue(a)
      const bValue = getValue(b)
      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      const comparison =
        (sortConfig.key === 'start' || sortConfig.key === 'end') &&
        !Number.isNaN(aDate) &&
        !Number.isNaN(bDate)
          ? aDate - bDate
          : collator.compare(aValue, bValue)

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [declarations, getStatusLabel, sortConfig])

  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  // Wirtualizacja: bez niej 6727 wierszy to ~40 tys. komponentow Reacta
  // (kazdy wiersz ma 4 Tooltipy) i ~258 tys. wezlow DOM - kazda zmiana filtra
  // albo zaznaczenia zawieszala karte na kilkadziesiat sekund.
  const rowVirtualizer = useVirtualizer({
    count: declarations.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 12,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0

  // Set zamiast .includes() na tablicy: przy 6727 wierszach i pelnym zaznaczeniu
  // liniowe wyszukiwanie na kazdy wiersz to ~45 mln porownan na jeden render.
  const selectedIdSet = React.useMemo(
    () => new Set(selectedDeclarationIds),
    [selectedDeclarationIds],
  )

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = hasPartiallySelectedVisible
    }
  }, [hasPartiallySelectedVisible])

  const columns = React.useMemo(() => {
    const base = getImiDeclarationsColumns(t)
    return [
      {
        title: '',
        key: null as string | null,
        type: 'string' as const,
        sort: false,
        headerContent: (
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allVisibleSelected}
            onChange={(e) => onToggleSelectAll(e.target.checked)}
            disabled={declarations.length === 0 || isSelectionLocked}
            aria-label="Zaznacz wszystkie widoczne deklaracje"
            className="tw-checkbox tw-checkbox-sm tw-checkbox-primary tw-rounded"
          />
        ),
      },
      ...base,
    ]
  }, [
    t,
    allVisibleSelected,
    declarations.length,
    isSelectionLocked,
    onToggleSelectAll,
  ])

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {isLoading ? (
        <div className="tw-grid tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : sortedDeclarations.length > 0 ? (
        <div className="tw-relative tw-h-full tw-overflow-hidden">
       
          {isFetching && (
            <>
              <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-[60] tw-h-0.5 tw-overflow-hidden tw-bg-primary/20">
                <div className={styles.indeterminateBar} />
              </div>
            </>
          )}
          <div
            ref={scrollRef}
            className={`tw-h-full tw-overflow-y-auto tw-bg-base-100 tw-transition-opacity ${
              isFetching
                ? 'tw-pointer-events-none tw-opacity-60'
                : 'tw-opacity-100'
            }`}
          >
            <table className="tw-relative tw-w-full tw-table-fixed tw-border-spacing-x-2">
              <colgroup>
                {COLUMN_WIDTHS.map((width, i) => (
                  <col key={i} style={{ width }} />
                ))}
              </colgroup>
              <TableHeader
                opacity
                rounded={false}
                disabledCheckboxToggle
                columns={columns}
                sortConfig={sortConfig}
                sticky
                onHeaderClick={handleHeaderClick}
              />
              <tbody>
                {paddingTop > 0 && (
                  <tr aria-hidden style={{ height: paddingTop }} />
                )}
                {virtualRows.map((virtualRow) => {
                  const item = sortedDeclarations[virtualRow.index]
                  return (
                    <ImiDeclarationRow
                      key={item.declarationId}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      item={item}
                      isSelected={selectedIdSet.has(item.declarationId)}
                      isSelectionLocked={isSelectionLocked}
                      busy={isBusy(item.declarationId)}
                      canWithdraw={isWithdrawable(item)}
                      isDraft={item.declarationStatus === 'DRAFT'}
                      statusLabel={getStatusLabel(item)}
                      rowIndex={virtualRow.index}
                      onToggleSelect={onToggleSelect}
                      onAction={onAction}
                    />
                  )
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden style={{ height: paddingBottom }} />
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              disabledCheckboxToggle
              columns={columns}
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
            />
          </table>
          <TableEmpty title={t('exportModule.no-imi-declarations')} />
        </>
      )}
    </section>
  )
}

export default ImiDeclarationsTable
